import axios from "axios";
import { message } from "antd";
// Com HttpOnly cookies, não precisamos verificar cookies no frontend
import { cleanupOldCookies } from "../../utils/imageUtils";
import { isPublicOnlyPath } from "../../routes/paths";
import { isLikelyPortuguese, translateKnownApiError } from "../../utils/errorHandling";

declare module "axios" {
  interface AxiosRequestConfig {
    _suppressErrorMessage?: boolean;
    _errorMessageShown?: boolean;
  }
}

// Variável global para controlar o estado de erro 403
let has403Error = false;
let setHas403ErrorCallback: ((hasError: boolean) => void) | null = null;
let setErrorMessageCallbackVar: ((message: string) => void) | null = null;

// Controle para evitar múltiplas tentativas simultâneas de refresh
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Função para registrar o callback do contexto de erro
export const setErrorContextCallback = (
  callback: (hasError: boolean) => void,
) => {
  setHas403ErrorCallback = callback;
};

// Função para registrar o callback da mensagem de erro
export const setErrorMessageCallback = (
  callback: (message: string) => void,
) => {
  setErrorMessageCallbackVar = callback;
};

// Função para resetar o erro 403
export const reset403Error = () => {
  has403Error = false;
  if (setHas403ErrorCallback) {
    setHas403ErrorCallback(false);
  }
  if (setErrorMessageCallbackVar) {
    setErrorMessageCallbackVar("");
  }
};

// Disparar manualmente o estado global de erro de assinatura (usado por chamadas
// que verificam explicitamente o endpoint de assinatura). Faz o mesmo que o
// interceptor quando detecta problemas de subscription: marca o estado 403,
// seta a mensagem e redireciona o usuário para Minha Conta.
export const triggerSubscriptionError = (
  _rawMessage?: string,
  redirectTo: string = "/dash/minha-conta/",
) => {
  try {
    const errorMsg =
      "Nenhuma assinatura ativa foi encontrada para esta conta. Confira se seus dados cadastrais estão atualizados e faça uma nova assinatura. Em caso de dúvidas, nossa equipe de suporte está à disposição.";

    has403Error = true;
    if (setHas403ErrorCallback) setHas403ErrorCallback(true);
    if (setErrorMessageCallbackVar) setErrorMessageCallbackVar(errorMsg);
    if (typeof window !== "undefined") {
      try {
        if (!window.location.pathname.includes(redirectTo)) {
          window.location.href = redirectTo;
        }
      } catch (e) {
        // ignore
      }
    }
  } catch (e) {
    // ignore errors in trigger
  }
};

// Função para determinar a BASE_URL do SSE (sem /api)
export const getSseBaseUrl = (): string => {
  const hostname = window.location.hostname;
  if (
    hostname === "dev.yuha.com.br" ||
    hostname === "localhost" ||
    hostname.includes("dev")
  ) {
    return "https://dev.yuha.com.br";
  }
  return "https://www.yuha.com.br";
};

// Função para determinar a BASE_URL baseada no domínio atual
const getBaseUrl = (): string => {
  const hostname = window.location.hostname;

  // Se estiver no domínio de desenvolvimento
  if (
    hostname === "dev.yuha.com.br" ||
    hostname === "localhost" ||
    hostname.includes("dev")
  ) {
    return "https://dev.yuha.com.br/api";
  }

  // Domínio de produção (padrão)
  return "https://www.yuha.com.br/api";
};

const BASE_URL = getBaseUrl();

// Configuração do axios com interceptors para cookies
export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Importante para enviar cookies
  maxContentLength: 50 * 1024 * 1024, // 50MB para conteúdo
  maxBodyLength: 50 * 1024 * 1024, // 50MB para body
  timeout: 60000, // 60 segundos de timeout
});

api.interceptors.request.use(
  (config) => {
    // Limpar cookies antigos antes de requisições grandes (para otimização de imagens)
    if (
      config.data &&
      typeof config.data === "string" &&
      config.data.length > 1024 * 1024
    ) {
      // 1MB
      cleanupOldCookies();
    }

    // Otimizar headers para requisições grandes
    if (config.data && typeof config.data === "object") {
      const dataString = JSON.stringify(config.data);
      if (dataString.length > 1024 * 1024) {
        // 1MB
        // Remover headers desnecessários para requisições grandes
        delete config.headers["Accept-Encoding"];
        delete config.headers["Cache-Control"];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => {
    // Exibir mensagens de sucesso retornadas pelo backend
    // O backend deve enviar { message: string, type?: 'success' | 'info' | 'warning' }
    const apiMessage = (response?.data && (response.data as any).message) as
      | string
      | undefined;
    const apiMessageType = (response?.data && (response.data as any).type) as
      | "success"
      | "info"
      | "warning"
      | undefined;

    // Suprimir mensagens informativas sobre ausência de integração (Meta/Google Ads)
    const shouldSuppress = (msg?: string) => {
      if (!msg) return false;
      const m = msg.toLowerCase();
      return (
        m.includes("user is not authenticated with meta") ||
        m.includes("perform integration with meta") ||
        m.includes("user is not authenticated with google") ||
        m.includes("perform integration with google")
      );
    };

    if (apiMessage && !shouldSuppress(apiMessage)) {
      if (apiMessageType === "info") {
        message.info(apiMessage);
      } else if (apiMessageType === "warning") {
        message.warning(apiMessage);
      } else {
        message.success(apiMessage);
      }
    }

    // Não resetar erro 403 automaticamente - apenas manualmente quando necessário
    // O erro só deve ser resetado quando o usuário realmente resolver o problema
    return response;
  },
  async (error) => {
    const suppressErrorMessage = Boolean(error.config?._suppressErrorMessage);

    // Tratar erro 431 - Request Header Fields Too Large
    if (error.response?.status === 431) {
      // Limpar cookies antigos que podem estar causando o problema
      cleanupOldCookies();

      // Tentar novamente a requisição se não foi uma tentativa de retry
      if (!error.config._retry && error.config.method !== "get") {
        error.config._retry = true;

        // Aguardar um pouco antes de tentar novamente
        await new Promise((resolve) => setTimeout(resolve, 1000));

        try {
          return await api.request(error.config);
        } catch (retryError) {
          message.error(
            "Erro ao processar dados. Tente novamente ou use imagens menores.",
          );
          return Promise.reject(retryError);
        }
      } else {
        message.error(
          "Dados muito grandes para processar. Tente com imagens menores ou limpe o cache do navegador.",
        );
        return Promise.reject(error);
      }
    }

    // Tratar erro 403
    if (error.response?.status === 403) {
      if (suppressErrorMessage) {
        return Promise.reject(error);
      }

      // Se estivermos na página de contratação de conta, não executar
      // o fluxo global de redirecionamento/flag para evitar reloads infinitos.
      try {
        if (
          typeof window !== "undefined" &&
          window.location.pathname.includes("/dash/contratar-conta/")
        ) {
          return Promise.reject(error);
        }
      } catch (e) {
        // ignore
      }
      // Não interferir em páginas públicas (ex.: vitrine pública)
      // Nem nos endpoints da vitrine no dashboard quando ainda não existe vitrine
      const url = error.config?.url || "";
      const isShowcaseEndpoint = url.includes("/showcase/");
      if (
        typeof window !== "undefined" &&
        (window.location.pathname.includes("/vitrine/") || isShowcaseEndpoint)
      ) {
        return Promise.reject(error);
      }

      const backendCode = error.response?.data?.code as string | undefined;
      const errorMessage = error.response?.data?.message || "";
      const errorMessageLower = errorMessage.toLowerCase();

      // Se for conta desativada, redirecionar para contratar conta
      if (backendCode === "NO_ACTIVE_ACCOUNT") {
        has403Error = true;
        const errorMsg =
          "Sua conta está desativada. Para acessar o painel, reative a conta ou contrate uma nova conta.";
        if (setHas403ErrorCallback) setHas403ErrorCallback(true);
        if (setErrorMessageCallbackVar) setErrorMessageCallbackVar(errorMsg);
        if (!window.location.pathname.includes("/dash/contratar-conta/")) {
          window.location.href = "/dash/contratar-conta/";
        }

        return Promise.reject(error);
      }

      // Verificar se é erro de subscription inativa
      const isSubscriptionError =
        errorMessageLower.includes(
          "account has no active or uncancelled subscription",
        ) ||
        errorMessageLower.includes("no active subscription") ||
        (errorMessageLower.includes("subscription") &&
          errorMessageLower.includes("active")) ||
        errorMessageLower ===
          "account has no active or uncancelled subscription";

      // Apenas para erros de assinatura: ativar estado global 403 e redirecionar
      if (isSubscriptionError) {
        has403Error = true;
        const errorMsg =
          "Nenhuma assinatura ativa foi encontrada para esta conta. Confira se seus dados cadastrais estão atualizados e faça uma nova assinatura. Em caso de dúvidas, nossa equipe de suporte está à disposição.";
        if (setHas403ErrorCallback) {
          setHas403ErrorCallback(true);
        }
        if (setErrorMessageCallbackVar) {
          setErrorMessageCallbackVar(errorMsg);
        }
        if (!window.location.pathname.includes("/dash/minha-conta/")) {
          window.location.href = "/dash/minha-conta/";
        }
      }

      // Para demais 403 (ex.: permissão/role): não bloquear navegação global
      return Promise.reject(error);
    }

    // Tratar erro 404 - verificar se é erro de subscription não encontrada
    if (error.response?.status === 404) {
      if (suppressErrorMessage) {
        return Promise.reject(error);
      }

      try {
        if (
          typeof window !== "undefined" &&
          window.location.pathname.includes("/dash/contratar-conta/")
        ) {
          return Promise.reject(error);
        }
      } catch (e) {
        // ignore
      }
      const url = error.config?.url || "";

      // Sem próxima fatura (NO_UPCOMING_INVOICE): a assinatura existe, só não
      // será renovada. Não é conta sem assinatura — a tela trata a ausência.
      if (
        url.includes("/billing/subscription/next-invoice") ||
        error.response?.data?.code === "NO_UPCOMING_INVOICE"
      ) {
        return Promise.reject(error);
      }

      const isSubscriptionEndpoint =
        url.includes("/subscriptions/current") ||
        url.includes("/billing/subscription");
      const isShowcaseEndpoint = url.includes("/showcase/");

      if (isSubscriptionEndpoint) {
        has403Error = true;

        const errorMsg =
          "Nenhuma assinatura ativa foi encontrada para esta conta. Confira se seus dados cadastrais estão atualizados e faça uma nova assinatura. Em caso de dúvidas, nossa equipe de suporte está à disposição.";

        if (setHas403ErrorCallback) {
          setHas403ErrorCallback(true);
        }

        if (setErrorMessageCallbackVar) {
          setErrorMessageCallbackVar(errorMsg);
        }

        // Redirecionar para Minha Conta se não estiver já lá
        if (!window.location.pathname.includes("/dash/minha-conta/")) {
          window.location.href = "/dash/minha-conta/";
        }

        return Promise.reject(error);
      }

      // Se for endpoint da vitrine (não encontrada), não redirecionar nem deslogar
      if (isShowcaseEndpoint) {
        return Promise.reject(error);
      }

      // Para outros 404 (incluindo payment/checkout), deixar o componente tratar
      // Não mostrar mensagem aqui para evitar duplicação
      return Promise.reject(error);
    }

    // Evitar loop infinito - não tentar refresh se já estamos tentando fazer refresh
    // Também não tentar refresh nas telas de entrada (login, cadastro,
    // parceiros, senha): o visitante anônimo não tem tokens válidos.
    if (
      error.response?.status === 401 &&
      !error.config._retry &&
      !error.config.url?.includes("/auth/refresh") &&
      !isPublicOnlyPath(window.location.pathname)
    ) {
      // Não tentar refresh automático para endpoints da vitrine pública (/public-showcase/)
      // Mas permitir refresh para endpoints de edição da vitrine no dashboard (/dash/)
      const url = error.config?.url || "";
      const isPublicShowcaseEndpoint = url.includes("/public-showcase/");
      const isInDashboard = window.location.pathname.includes("/dash/");

      if (
        isPublicShowcaseEndpoint ||
        (!isInDashboard && url.includes("/showcase/"))
      ) {
        return Promise.reject(error);
      }
      error.config._retry = true;

      const refreshSuccess = await refreshToken();
      if (refreshSuccess) {
        // Reenviar a requisição original
        return api.request(error.config);
      } else {
        // Refresh falhou: backend já limpou os cookies, só redirecionar
        window.location.href = "/signin/";
        return Promise.reject(new Error("Refresh token failed"));
      }
    }

    // Verificar se o erro já foi tratado por um componente
    if ((error as any)._errorMessageShown) {
      return Promise.reject(error);
    }

    // Endpoints de autenticação (login/register) têm suas mensagens tratadas
    // diretamente pelos componentes de página — não exibir aqui para evitar duplicação
    const requestUrl = error.config?.url || "";
    if (
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/register")
    ) {
      return Promise.reject(error);
    }

    // Mostrar mensagens de erro retornadas pelo backend quando existirem,
    // exceto as mensagens informativas sobre ausência de integração (Meta/Google Ads)
    const backendMessage = error?.response?.data?.message as string | undefined;
    const backendCode = error?.response?.data?.code as string | undefined;
    const shouldSuppress = (
      msg?: string,
      code?: string,
      url?: string,
      status?: number,
    ) => {
      const requestPath = (url || "").toLowerCase();

      // Falhas no endpoint de próxima fatura não devem gerar toast.
      if (requestPath.includes("/billing/subscription/next-invoice")) {
        return true;
      }

      // Para o preview de troca de plano, suprimir apenas falhas internas/inesperadas.
      if (requestPath.includes("/billing/subscription/update-preview")) {
        const normalized = (msg || "").toLowerCase();
        if (
          status === 500 ||
          normalized.includes("unexpected error") ||
          normalized.includes("internal server error")
        ) {
          return true;
        }
      }

      if (code === "NO_PAYMENT_METHOD") return true;
      if (!msg) return false;
      const m = msg.toLowerCase();

      // Finance manager não possui acesso ao endpoint de usuários;
      // evitar toast repetitivo durante refresh automático.
      if (requestPath.includes("/users") && m.includes("not authorized")) {
        return true;
      }

      return (
        m.includes("user is not authenticated with meta") ||
        m.includes("perform integration with meta") ||
        m.includes("user is not authenticated with google") ||
        m.includes("perform integration with google") ||
        // Suprimir erro específico quando a conta/vitrine não existe ainda
        m.includes("account not found with the given id") ||
        // Falha de token/autenticação: sem toast (o fluxo de sessão já trata)
        m.includes("invalid token") ||
        m.includes("authentication failed") ||
        // Suprimir erros de validação de entrada (deixar componentes tratarem)
        m.includes("validate input error") ||
        m.includes("invalid input") ||
        m.includes("entity: must be a valid entity")
      );
    };

    // Personalizar mensagens específicas de erro - abrangente para todo o sistema
    const getCustomErrorMessage = (msg?: string): string | null => {
      if (!msg) return null;
      const m = msg.toLowerCase();

      // Erros de conta e usuários
      if (
        m.includes("you cannot deactivate your own account") ||
        m.includes("cannot deactivate own account")
      ) {
        return "Você não pode desativar sua própria conta.";
      }

      if (m.includes("user not found") || m.includes("account not found")) {
        return "Usuário ou conta não encontrado.";
      }

      if (
        m.includes("user already exists") ||
        m.includes("account already exists")
      ) {
        return "Este usuário ou conta já existe.";
      }

      if (m.includes("invalid email") || m.includes("email format")) {
        return "Não foi possível fazer login. Verifique seu e-mail e senha.";
      }

      if (m.includes("password too weak") || m.includes("weak password")) {
        return "A senha deve ter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas e números.";
      }

      if (m.includes("cannot set finance communication emails to 0")) {
        return "Não é possível desabilitar a comunicação financeira. Pelo menos um usuário deve ter essa opção habilitada.";
      }

      if (
        m.includes("surpass allowed amount of active users") ||
        m.includes("update will surpass allowed amount of active users")
      ) {
        return "Downgrade não permitido: você possui usuários ativos além do limite do plano selecionado.";
      }

      if (
        m.includes("data retention product already exists for account") ||
        (m.includes("data retention") && m.includes("already exists"))
      ) {
        return "Sua conta já possui um produto de retenção de dados ativo. Cancele o atual para contratar outro.";
      }

      // Erros de cupom e promoção
      if (
        m.includes("promotion code unavailable") ||
        m.includes("invalid promotion code") ||
        m.includes("promotion code not found")
      ) {
        return "O cupom informado não está disponível para este plano.";
      }

      if (m.includes("promotion code expired")) {
        return "O cupom informado está expirado.";
      }

      if (m.includes("promotion code")) {
        return "Não foi possível aplicar o cupom informado. Verifique e tente novamente.";
      }

      // Erros de permissões e acesso
      if (
        m.includes("insufficient permissions") ||
        m.includes("permission denied") ||
        m.includes("access denied")
      ) {
        return "Você não tem permissão para realizar esta ação.";
      }

      if (m.includes("role not found") || m.includes("invalid role")) {
        return "Função ou perfil inválido.";
      }

      // Erros de integração
      if (m.includes("integration failed") || m.includes("connection failed")) {
        return "Falha na integração. Verifique suas credenciais e tente novamente.";
      }

      if (m.includes("api key invalid") || m.includes("invalid credentials")) {
        return "Credenciais inválidas. Verifique suas chaves de API.";
      }

      // Erros de dados e validação
      if (m.includes("invalid data") || m.includes("validation error")) {
        return "Dados inválidos. Verifique as informações e tente novamente.";
      }

      if (m.includes("required field") || m.includes("field is required")) {
        return "Campo obrigatório não preenchido.";
      }

      if (m.includes("invalid format") || m.includes("format error")) {
        return "Formato inválido. Verifique os dados inseridos.";
      }

      // Erros de limite e quota
      if (
        m.includes("limit exceeded") ||
        m.includes("quota exceeded") ||
        m.includes("usage limit")
      ) {
        return "Limite excedido. Considere fazer um upgrade do plano.";
      }

      if (m.includes("rate limit") || m.includes("too many requests")) {
        return "Muitas tentativas. Aguarde alguns minutos e tente novamente.";
      }

      // Erros de arquivo e upload
      if (m.includes("file too large") || m.includes("file size")) {
        return "Arquivo muito grande. Reduza o tamanho e tente novamente.";
      }

      if (m.includes("invalid file type") || m.includes("unsupported format")) {
        return "Tipo de arquivo não suportado.";
      }

      if (m.includes("upload failed")) {
        return "Falha no upload. Verifique sua conexão e tente novamente.";
      }

      // Erros de sistema
      if (m.includes("service unavailable") || m.includes("maintenance")) {
        return "Serviço temporariamente indisponível. Tente novamente em alguns minutos.";
      }

      if (
        m.includes("internal server error") ||
        m.includes("unexpected error")
      ) {
        return "Erro interno do servidor. Tente novamente em alguns instantes.";
      }

      // Erros de sessão e autenticação
      if (m.includes("session expired") || m.includes("token expired")) {
        return "Sessão expirada. Faça login novamente.";
      }

      // Se não encontrou mensagem customizada, retorna null para usar a mensagem original (se apropriada)
      return null;
    };

    // Determinar se deve mostrar mensagem de erro
    let shouldShowErrorMessage = false;
    let errorMessageToShow = "";
    const isSuppressedRequest = shouldSuppress(
      backendMessage,
      backendCode,
      requestUrl,
      error?.response?.status,
    );

    // Mapeamento do interceptor e, na falta dele, o dicionário compartilhado
    // de traduções (o mesmo usado pelas telas).
    const customMessage =
      getCustomErrorMessage(backendMessage) ?? (backendMessage ? translateKnownApiError(backendMessage) : null);
    if (isSuppressedRequest) {
      shouldShowErrorMessage = false;
    } else if (customMessage) {
      shouldShowErrorMessage = true;
      errorMessageToShow = customMessage;
    } else if (backendMessage && !isSuppressedRequest) {
      // Verificar se é uma mensagem técnica que deve ser substituída
      const isTechnicalMessage =
        backendMessage.toLowerCase().includes("request failed") ||
        backendMessage.toLowerCase().includes("status code") ||
        backendMessage.toLowerCase().includes("network error") ||
        backendMessage.toLowerCase().includes("timeout");

      shouldShowErrorMessage = true;
      if (isTechnicalMessage) {
        errorMessageToShow =
          "Erro de conexão. Verifique sua internet e tente novamente.";
      } else if (isLikelyPortuguese(backendMessage)) {
        // Mensagem já está em português — exibir diretamente
        errorMessageToShow = backendMessage;
      } else {
        // Mensagem em inglês sem mapeamento — usar fallback genérico em PT-BR
        errorMessageToShow = "Ocorreu um erro inesperado. Tente novamente.";
      }
    } else if (!error.response && error.message) {
      // Erro de rede sem resposta do servidor
      shouldShowErrorMessage = true;
      errorMessageToShow =
        "Erro de conexão. Verifique sua internet e tente novamente.";
    }

    if (shouldShowErrorMessage && !suppressErrorMessage) {
      message.error(errorMessageToShow);
      // Marcar o erro como já tratado para evitar duplicação nos componentes
      try {
        (error as any)._errorMessageShown = true;
        if (error.config) (error.config as any)._errorMessageShown = true;
      } catch (e) {
        // ignore
      }
    }

    return Promise.reject(error);
  },
);

// Com HttpOnly cookies, não podemos verificar autenticação no frontend
// O backend gerencia automaticamente via cookies

// Função para refresh do token com controle de concorrência
export const refreshToken = async (): Promise<boolean> => {
  // Se já existe um refresh em andamento, aguardar o resultado
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Iniciar novo refresh
  isRefreshing = true;
  refreshPromise = (async (): Promise<boolean> => {
    try {
      // Com HttpOnly cookies, o backend automaticamente verifica o refresh token
      // Usar axios diretamente para evitar o interceptor
      const response = await axios.post(
        `${BASE_URL}/auth/refresh`,
        {},
        {
          withCredentials: true,
        },
      );

      return true;
    } catch (error: any) {
      // Se o refresh token expirou ou é inválido, o backend limpa os cookies
      return false;
    } finally {
      // Resetar flags após conclusão
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Função para logout (invalidar tokens)
export const logoutUser = async (): Promise<boolean> => {
  try {
    // Usar axios diretamente para evitar o interceptor
    const response = await axios.delete(`${BASE_URL}/auth/refresh`, {
      withCredentials: true,
    });
    return true;
  } catch (error) {
    return false;
  }
};

const getTimeZoneOffset = (date: Date, timeZone: string): string => {
  const offsetText = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;

  const match = offsetText?.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/);
  if (!match) {
    return "-03:00";
  }

  const hour = Number(match[1]);
  const minute = Number(match[2] || "0");
  const signal = hour >= 0 ? "+" : "-";
  return `${signal}${String(Math.abs(hour)).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const formatDateForAPI = (date: Date): string => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");
  const offset = getTimeZoneOffset(date, "America/Sao_Paulo");

  return `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds}${offset}`;
};

export const getDateRangeByPeriod = (
  period:
    | "today"
    | "yesterday"
    | "last7"
    | "currentMonth"
    | "lastMonth"
    | "last30"
    | "last60",
): { start: string; end: string } => {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  switch (period) {
    case "today":
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;

    case "yesterday":
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
      break;

    case "last7":
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;

    case "currentMonth":
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;

    case "lastMonth":
      start.setMonth(start.getMonth() - 1);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setDate(0);
      end.setHours(23, 59, 59, 999);
      break;

    case "last30":
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;

    case "last60":
      start.setDate(start.getDate() - 59);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;

    default:
      start.setTime(Date.now() - 60 * 60 * 1000);
      end.setTime(Date.now());
      break;
  }

  return {
    start: formatDateForAPI(start),
    end: formatDateForAPI(end),
  };
};
